import { Tab, TabList } from '@chakra-ui/react'
import * as React from 'react'

import { useTheme } from '~theme/ThemeProvider'
import { TActiveColorKey } from '~theme/typings'
import { getActiveColorFullPath, getColorValue } from '~theme/utils'
import { Grid, GridRow } from '~ui/components/layout/Box'
import { getSpacingValue } from '~ui/styleGuide/spaces'

type TSetTabIndex = React.Dispatch<React.SetStateAction<number>>

type TRenderNavButtonOptions = {
  type: 'prev' | 'next'
  isDisabled: boolean
  onClick: () => void
}

export type TFooterProps = NoChildren & {
  /**
   * number of tabs
   */
  tabsCount: number
  /**
   * index of the currently selected tab
   */
  selectedIndex: number
  /**
   * function to set the index of the currently selected tab
   */
  setTabIndex: TSetTabIndex
  /**
   * background color of the unselected navigation radio
   */
  bgColorNavCircle: TActiveColorKey
  /**
   * background color of the selected navigation radio
   */
  bgColorSelected: TActiveColorKey
  /**
   * function to render the navigation buttons
   */
  renderNavButton: (options: TRenderNavButtonOptions) => React.ReactNode
}

const CIRCLE_SIZE = `0.5rem`
const SELECTED_CIRCLE_SIZE = `0.75rem`

/**
 * Navigation for the Carousel component.
 * Renders the navigation as a list of bullet points and left/right buttons.
 */
export const CarouselNavigation: React.FC<TFooterProps> = ({
  tabsCount,
  setTabIndex,
  selectedIndex,
  bgColorNavCircle,
  bgColorSelected,
  renderNavButton,
}) => {
  const theme = useTheme()

  const setNext = () => {
    setTabIndex((prev) => prev + 1)
  }

  const setPrevious = () => {
    setTabIndex((prev) => prev - 1)
  }

  const isFirst = selectedIndex === 0
  const isLast = selectedIndex === tabsCount - 1

  // color of the unselected radio of the navigation
  const bgColorValue = React.useMemo(() => {
    const fullKey = getActiveColorFullPath({ bgColor: bgColorNavCircle })
    return getColorValue(theme)(fullKey)
  }, [bgColorNavCircle, theme])

  // color of the selected radio of the navigation
  const selectedBgColorValue = React.useMemo(() => {
    const fullKey = getActiveColorFullPath({ bgColor: bgColorSelected })
    return getColorValue(theme)(fullKey)
  }, [bgColorSelected, theme])

  // create an array of ids for the bullet-point navigation
  const buttons = React.useMemo(() => {
    return createArrayFrom(tabsCount)
  }, [tabsCount])

  return (
    <Grid padding={{ vertical: `none`, horizontal: `mediumLarge` }}>
      {/* Navigation made of bullet points */}
      <TabList
        paddingTop={getSpacingValue(`medium`)}
        justifySelf={`center`}
        alignItems={`center`}
        gap={getSpacingValue(`smallMedium`)}
      >
        {buttons.map(({ id }) => (
          <Tab
            key={id}
            borderRadius={`100%`}
            width={CIRCLE_SIZE}
            height={CIRCLE_SIZE}
            bgColor={bgColorValue}
            _selected={{
              bgColor: selectedBgColorValue,
              width: SELECTED_CIRCLE_SIZE,
              height: SELECTED_CIRCLE_SIZE,
            }}
          />
        ))}
      </TabList>

      {/* Navigation made of left & right buttons */}
      <GridRow justifyContent={`space-between`}>
        {renderNavButton({ type: 'prev', isDisabled: isFirst, onClick: setPrevious })}
        {renderNavButton({ type: 'next', isDisabled: isLast, onClick: setNext })}
      </GridRow>
    </Grid>
  )
}

// * HELPERS

// maps an index to an object with an id property,
// can be used in Array.from second argument to create an array of objects with an id property
const useIndexAsId = (_: unknown, index: number) => ({ id: index })

// create an array of objects with an id property
const createArrayFrom = (length: number) => Array.from({ length }, useIndexAsId)
